<template>
    <div class="min-h-screen bg-white">
        <div v-if="loading" class="flex items-center justify-center min-h-[60vh]">
            <Loader size="large" />
        </div>
        <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div class="text-center mb-2.5">
                <h1 class="font-header text-[3rem] font-bold text-primary mb-1.5">About Us</h1>
                <div v-if="pageData?.description"
                    class="font-body text-[1.125rem] leading-[1.75] text-gray-700 max-w-4xl mx-auto content-page-description"
                    v-html="pageData.description">
                </div>
            </div>

            <div v-if="pageData?.sections && pageData.sections.length > 0"
                class="max-w-4xl mx-auto space-y-[0.196rem] mb-0">
                <div v-for="section in pageData.sections" :key="section.title"
                    class="bg-white rounded-lg shadow-sm p-2.5 md:p-4">
                    <h2 v-if="section.title" class="font-header text-[1.5rem] font-bold text-primary mb-1">{{
                        section.title }}</h2>
                    <div v-if="section.title === 'Our Values' && parsedValues.length > 0" class="space-y-1">
                        <div v-if="valuesIntro"
                            class="font-body text-[1.125rem] leading-[1.75] text-gray-700 mb-1 content-page-section"
                            v-html="valuesIntro"></div>
                        <div v-for="value in parsedValues" :key="value.title" class="space-y-1">
                            <h3 class="font-header text-[1.5rem] font-bold text-primary mb-1">{{ value.title }}</h3>
                            <div class="font-body text-[1.125rem] leading-[1.75] text-gray-700 content-page-section"
                                v-html="value.content">
                            </div>
                        </div>
                    </div>
                    <div v-else class="font-body text-[1.125rem] leading-[1.75] text-gray-700 content-page-section"
                        v-html="section.content">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'

const pageData = ref(null)
const { loading, setLoading } = useLoadingState(true)

const valuesIntro = computed(() => {
    if (!pageData.value) return ''
    const valuesSection = pageData.value.sections?.find(s => s.title === 'Our Values')
    if (!valuesSection) return ''

    const content = valuesSection.content
    const firstValueIndex = content.indexOf('###')

    if (firstValueIndex > 0) {
        return content.substring(0, firstValueIndex).trim()
    } else if (firstValueIndex === -1) {
        return content.trim()
    }
    return ''
})

const parsedValues = computed(() => {
    if (!pageData.value) return []
    const valuesSection = pageData.value.sections?.find(s => s.title === 'Our Values')
    if (!valuesSection) return []

    const values = []
    const valuePattern = /###\s*(.+?)\n\n(.+?)(?=\n\n###|$)/gs
    let match
    while ((match = valuePattern.exec(valuesSection.content)) !== null) {
        values.push({
            title: match[1].trim(),
            content: match[2].trim()
        })
    }
    return values
})

const loadAboutPage = async () => {
    setLoading(true)
    try {
        const data = await publicService.getPage('About Us')
        pageData.value = data
    } catch (error) {
        console.error('Failed to load About page:', error)
        pageData.value = {
            title: 'About Us',
            description: '<i>Barry & Bonna Publishing</i> LLC is a publishing house that seeks to revolutionize the world of storytelling and book distribution by empowering voices from all backgrounds.',
            sections: []
        }
    } finally {
        setLoading(false)
    }
}

onMounted(() => {
    loadAboutPage()
})
</script>

<style scoped>
/* Links */
:deep(a) {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.2s;
    font-family: inherit;
}

:deep(a):hover {
    color: #1e40af;
}

/* Lists */
:deep(ul) {
    list-style-type: disc;
    margin-left: 1.5em;
    margin-bottom: 0.1em;
}

:deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5em;
    margin-bottom: 0.1em;
}

:deep(li) {
    margin-bottom: 0.05em;
}

/* Headers */
:deep(h1) {
    font-size: 2rem;
    font-weight: bold;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    color: #2D4D31;
}

:deep(h2) {
    font-size: 1.75rem;
    font-weight: bold;
    margin-top: 1.25rem;
    margin-bottom: 0.875rem;
    color: #2D4D31;
}

:deep(h3) {
    font-size: 1.5rem;
    font-weight: bold;
    margin-top: 1rem;
    margin-bottom: 0.75rem;
    color: #2D4D31;
}

/* Font Sizes */
:deep(.ql-size-small) {
    font-size: 0.875rem;
}

:deep(.ql-size-large) {
    font-size: 1.25rem;
}

:deep(.ql-size-huge) {
    font-size: 1.5rem;
}

/* Blockquotes */
:deep(blockquote) {
    border-left: 4px solid #2D4D31;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #5B4F47;
}

/* Code Blocks */
:deep(pre) {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 0.25rem;
    padding: 1rem;
    overflow-x: auto;
    margin: 1rem 0;
    font-family: 'Courier New', monospace;
}

:deep(code) {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
}

:deep(pre code) {
    background: none;
    border: none;
    padding: 0;
}

/* Images */
:deep(img) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1.5rem auto;
    border-radius: 0.5rem;
}

/* Videos */
:deep(iframe) {
    max-width: 100%;
    margin: 1.5rem auto;
    display: block;
    border-radius: 0.5rem;
}

:deep(.ql-video) {
    width: 100%;
    max-width: 640px;
    height: 360px;
    margin: 1.5rem auto;
    display: block;
}

/* Text Alignment */
:deep(.ql-align-center) {
    text-align: center;
}

:deep(.ql-align-right) {
    text-align: right;
}

:deep(.ql-align-left) {
    text-align: left;
}

:deep(.ql-align-justify) {
    text-align: justify;
}

/* Inline styles support */
:deep([style*="text-align: center"]),
:deep([style*="text-align:center"]) {
    text-align: center !important;
}

:deep([style*="text-align: right"]),
:deep([style*="text-align:right"]) {
    text-align: right !important;
}

:deep([style*="text-align: left"]),
:deep([style*="text-align:left"]) {
    text-align: left !important;
}

:deep([style*="text-align: justify"]),
:deep([style*="text-align:justify"]) {
    text-align: justify !important;
}

/* Font Families */
:deep(.ql-font-serif) {
    font-family: Georgia, 'Times New Roman', serif;
}

:deep(.ql-font-monospace) {
    font-family: 'Courier New', monospace;
}

/* Spacing */
:deep(p) {
    margin-bottom: 0.75rem;
}

:deep(p:last-child) {
    margin-bottom: 0;
}
</style>
